import React, { useMemo, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Underline from '@tiptap/extension-underline';
import { Box } from '@mui/material';

interface RichTextDisplayProps {
  content: string | object;
  maxLength?: number; // Optional: truncate content to this length (in plain text)
}

// Define extensions once outside component to avoid recreation
const getDisplayExtensions = () => [
  StarterKit.configure({
    // Exclude link and underline from StarterKit since we're adding configured versions
    link: false,
    underline: false,
  }),
  Underline,
  Link.configure({
    openOnClick: true,
    HTMLAttributes: {
      class: 'tiptap-link',
    },
  }),
  Mention.configure({
    HTMLAttributes: {
      class: 'tiptap-mention',
    },
    renderHTML({ options, node }) {
      return [
        'span',
        {
          class: options.HTMLAttributes.class,
          'data-mention-id': node.attrs.id,
        },
        `@${node.attrs.label || node.attrs.id}`,
      ];
    },
  }),
];

const RichTextDisplay: React.FC<RichTextDisplayProps> = ({ content, maxLength }) => {
  // Convert content to Tiptap JSON format
  const getTiptapContent = () => {
    if (!content) {
      return null;
    }

    // If content is already an object, use it directly
    if (typeof content === 'object') {
      return content;
    }

    // If content is a JSON string, parse it
    if (typeof content === 'string') {
      try {
        return JSON.parse(content);
      } catch {
        // If not valid JSON, treat as plain text and convert to Tiptap format
        return {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: content }],
            },
          ],
        };
      }
    }

    return null;
  };

  const tiptapContent = getTiptapContent();

  // Memoize extensions
  const extensions = useMemo(() => getDisplayExtensions(), []);

  // Create a read-only editor
  const editor = useEditor({
    extensions,
    content: tiptapContent,
    editable: false,
    editorProps: {
      attributes: {
        class: 'tiptap-display',
      },
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  // Truncate content if maxLength is specified
  React.useEffect(() => {
    if (editor && maxLength) {
      const text = editor.getText();
      if (text.length > maxLength) {
        // Create truncated content
        const truncatedText = text.substring(0, maxLength) + '...';
        editor.commands.setContent({
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: truncatedText }],
            },
          ],
        });
      }
    }
  }, [editor, maxLength]);

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        '& .tiptap-display': {
          outline: 'none',
          '& p': {
            margin: 0,
            lineHeight: 1.4,
          },
          '& strong': {
            fontWeight: 600,
          },
          '& em': {
            fontStyle: 'italic',
          },
          '& u': {
            textDecoration: 'underline',
          },
          // Headings
          '& h1': {
            fontSize: '2em',
            fontWeight: 'bold',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          '& h2': {
            fontSize: '1.5em',
            fontWeight: 'bold',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          '& h3': {
            fontSize: '1.25em',
            fontWeight: 'bold',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          // Lists
          '& ul, & ol': {
            paddingLeft: '1.5em',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          '& li': {
            marginBottom: '0.25em',
          },
          // Blockquote
          '& blockquote': {
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            paddingLeft: '1em',
            marginLeft: 0,
            marginTop: '0.5em',
            marginBottom: '0.5em',
            fontStyle: 'italic',
            color: 'text.secondary',
          },
          // Code block
          '& pre': {
            backgroundColor: 'grey.100',
            borderRadius: '4px',
            padding: '1em',
            marginTop: '0.5em',
            marginBottom: '0.5em',
            overflow: 'auto',
            '& code': {
              fontFamily: 'monospace',
              fontSize: '0.875em',
            },
          },
          // Inline code
          '& code': {
            backgroundColor: 'grey.100',
            borderRadius: '4px',
            padding: '2px 4px',
            fontFamily: 'monospace',
            fontSize: '0.875em',
          },
          // Horizontal rule
          '& hr': {
            border: 'none',
            borderTop: '2px solid',
            borderColor: 'divider',
            marginTop: '1em',
            marginBottom: '1em',
          },
          // Strikethrough
          '& s': {
            textDecoration: 'line-through',
          },
          // Link
          '& .tiptap-link': {
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.dark',
            },
          },
          // Mention
          '& .tiptap-mention': {
            color: 'primary.main',
            backgroundColor: 'action.hover',
            borderRadius: '4px',
            padding: '2px 4px',
            fontWeight: 500,
          },
        },
      }}
    >
      <EditorContent editor={editor} />
    </Box>
  );
};

export default RichTextDisplay;